import { memo, useCallback, useState } from 'react';

import { type AirActionTrackingLocation, useTrackCanvasMoved } from '@air/analytics';
import { type Board } from '@air/api/types';
import { TreeItemButton, treeItem } from '@air/component-tree';
import { Check, Lock } from '@air/next-icons';
import { Button } from '@air/primitive-button';
import { Modal, ModalCloseButton, ModalTitle } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useSingleBoardSearchState } from '~/components/BoardSearch/shared/hooks';
import { PrivateBoardSelect } from '~/components/BoardSelect/PrivateBoardSelect';
import { useBoardPermissionsCache } from '~/hooks/useBoardPermissionsCache';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { usePrivateCanvasesUpdateCanvasMutation } from '~/swr-hooks/canvases/useUpdateCanvas';
import { canCreateCanvasInBoard } from '~/utils/permissions/boardPermissions';

const isLibraryDisabled = () => true;
const getLibraryDisabledMessage = () => 'Canvases must be moved to a board';

export type MoveCanvasToBoardModalProps = {
  canvasIds: string[];
  /** Current board the canvases are on; this board will be disabled in the picker */
  currentBoardId: string | undefined;
  trackLocation: AirActionTrackingLocation;
};

export const MoveCanvasToBoardModal = memo(
  ({
    canvasIds,
    currentBoardId,
    onClose,
    trackLocation: _trackLocation,
  }: AirModalProps<MoveCanvasToBoardModalProps>) => {
    const { currentWorkspace } = useCurrentWorkspace();
    const {
      selectedBoard,
      isBoardSelected,
      onSelectBoard: onSelectBoardFromSearch,
      shouldShowSubBoards,
      onSelectLibrary,
      isLibrarySelected,
    } = useSingleBoardSearchState();
    const [isMyCanvasesSelected, setIsMyCanvasesSelected] = useState(false);

    const { getBoardPermissions } = useBoardPermissionsCache();
    const { showToast } = useToasts();
    const { mutateAsync: updateCanvas, isPending } = usePrivateCanvasesUpdateCanvasMutation();
    const { trackCanvasMoved } = useTrackCanvasMoved();
    const workspaceId = currentWorkspace?.id;

    const onSelectBoard = useCallback(
      (board: Parameters<NonNullable<typeof onSelectBoardFromSearch>>[0]) => {
        setIsMyCanvasesSelected(false);
        onSelectBoardFromSearch(board);
      },
      [onSelectBoardFromSearch],
    );

    const isLocationSelected = isMyCanvasesSelected || (!!selectedBoard && selectedBoard.id !== 'root');

    const getBoardDisabledMessage = useCallback(
      (board: Board) => {
        const boardPermissions = getBoardPermissions(board.id);

        if (currentBoardId && board.id === currentBoardId) {
          return "You can't select the current board";
        }

        if (!canCreateCanvasInBoard(boardPermissions)) {
          return "You don't have permission to add canvases to this board";
        }

        return undefined;
      },
      [currentBoardId, getBoardPermissions],
    );

    const isBoardDisabled = useCallback(
      (board: Board) => {
        const boardPermissions = getBoardPermissions(board.id);

        if (currentBoardId && board.id === currentBoardId) {
          return true;
        }

        return !canCreateCanvasInBoard(boardPermissions);
      },
      [currentBoardId, getBoardPermissions],
    );

    const count = canvasIds.length;

    const handleMove = useCallback(async () => {
      if (!workspaceId) {
        return;
      }

      if (!isMyCanvasesSelected && (!selectedBoard || selectedBoard.id === 'root')) {
        return;
      }

      const destinationBoardId = isMyCanvasesSelected ? null : selectedBoard?.id;

      try {
        await updateCanvas({
          workspaceId,
          canvases: canvasIds.map((id) => ({ canvasId: id, boardId: destinationBoardId })),
        });
      } catch (error) {
        showToast('There was an error updating the canvas.');
        reportErrorToBugsnag({
          error,
          context: 'Failed to update canvas',
          metadata: { canvasIds },
        });
        return;
      }

      for (const canvasId of canvasIds) {
        trackCanvasMoved({
          canvas_id: canvasId,
          original_location: currentBoardId ?? 'canvases',
          new_location: destinationBoardId ?? 'canvases',
        });
      }

      const destinationName = isMyCanvasesSelected ? 'My canvases' : selectedBoard?.title;
      const toastMessage =
        count === 1 ? `Canvas moved to ${destinationName}` : `${count} canvases moved to ${destinationName}`;

      onClose();
      showToast(toastMessage);
    }, [
      canvasIds,
      count,
      currentBoardId,
      isMyCanvasesSelected,
      onClose,
      selectedBoard,
      showToast,
      trackCanvasMoved,
      updateCanvas,
      workspaceId,
    ]);

    return (
      <Modal
        className="flex flex-col gap-6"
        dangerouslyBypassFocusLock
        data-testid="MOVE_CANVAS_TO_BOARD_MODAL"
        isOpen
        onDismiss={onClose}
      >
        <header className="flex items-center justify-between gap-2">
          <ModalTitle data-testid="MOVE_CANVAS_TO_BOARD_MODAL_TITLE">Move to...</ModalTitle>
          <ModalCloseButton onClick={onClose} />
        </header>

        <PrivateBoardSelect
          getBoardDisabledMessage={getBoardDisabledMessage}
          getLibraryDisabledMessage={getLibraryDisabledMessage}
          isBoardDisabled={isBoardDisabled}
          isBoardSelected={isBoardSelected}
          isLibraryDisabled={isLibraryDisabled}
          isLibrarySelected={isLibrarySelected}
          onSelectBoard={onSelectBoard}
          onSelectLibrary={onSelectLibrary}
          shouldShowSubBoards={shouldShowSubBoards}
          topContent={
            <div
              className={treeItem({
                size: 'medium',
                state: isMyCanvasesSelected ? 'selected' : 'default',
                class: 'mb-2',
              })}
            >
              <div className="size-4 shrink-0" />
              <div className="flex size-6 shrink-0 items-center justify-center rounded-4 bg-grey-4">
                <Lock className="size-4" />
              </div>
              <TreeItemButton
                disabled={currentBoardId === undefined}
                onClick={() => {
                  setIsMyCanvasesSelected(true);
                  onSelectBoardFromSearch(null);
                }}
              >
                My canvases
              </TreeItemButton>
              {isMyCanvasesSelected && (
                <div className="flex size-4 shrink-0 items-center justify-center rounded-full bg-blue-3">
                  <Check className="size-3 text-blue-11" />
                </div>
              )}
            </div>
          }
        />

        <footer className="flex items-center justify-end gap-2">
          <Button appearance="ghost" color="grey" onClick={onClose} size="large">
            Cancel
          </Button>
          <Button
            appearance="filled"
            color="blue"
            data-testid="MOVE_CANVAS_TO_BOARD_MODAL_MOVE_BUTTON"
            disabled={!isLocationSelected}
            isLoading={isPending}
            onClick={handleMove}
            size="large"
          >
            Move
          </Button>
        </footer>
      </Modal>
    );
  },
);

MoveCanvasToBoardModal.displayName = 'MoveCanvasToBoardModal';
